import { create } from "zustand";
import type { MePermissions, PermissionScope, ProjectAction, TeamAction } from "@domain/schemas/workspace";

export type PermissionFetchStatus = "idle" | "loading" | "loaded" | "error";

/**
 * Keys a cache entry by scope. `teamId` and `projectId` are requested together,
 * so the pair - not either id alone - identifies an answer.
 */
const toScopeKey = (scope: PermissionScope): string => `${scope.teamId ?? ""}|${scope.projectId ?? ""}`;

type PermissionEntry = {
  permissions: MePermissions | undefined;
  status: PermissionFetchStatus;
  error: unknown;
};

const emptyEntry: PermissionEntry = { permissions: undefined, status: "idle", error: undefined };

type PermissionStore = {
  /**
  Last answer per scope. Scopes are not evicted; the set is bounded by the user's own teams and projects.
  */
  entries: Record<string, PermissionEntry>;
  /**
  The scope the UI is currently gating on.
  */
  activeScope: PermissionScope;
  /**
   * Deployment-wide admin flag, lifted out of the per-scope entries because it is
   * scope-independent and is needed before any scope has resolved.
   */
  isAdmin: boolean;
  /**
   * Bumped by {@link PermissionStore.invalidate}.
   *
   * Dropping a cache entry is not enough on its own to get it refilled: the
   * hydration effect keys off the active ids and the workspace status, neither
   * of which changes when a cached answer is discarded. Without a value that
   * does change, a denial-triggered invalidation leaves the scope permanently
   * unresolved and every gated control disabled until the user switches context
   * or reloads.
   */
  refetchNonce: number;

  setActiveScope: (scope: PermissionScope) => void;
  markLoading: (scope: PermissionScope) => void;
  setPermissions: (scope: PermissionScope, permissions: MePermissions) => void;
  setError: (scope: PermissionScope, error: unknown) => void;
  /**
  Drops cached answers so the next read refetches. Used after a denial contradicts the cache.
  */
  invalidate: (scope?: PermissionScope) => void;
  clear: () => void;
};

const emptyScope: PermissionScope = {};

export const usePermissionStore = create<PermissionStore>((set) => ({
  entries: {},
  activeScope: emptyScope,
  isAdmin: false,
  refetchNonce: 0,

  setActiveScope: (scope) => set({ activeScope: scope }),

  markLoading: (scope) =>
    set((state) => {
      const key = toScopeKey(scope);
      const previous = state.entries[key] ?? emptyEntry;
      // Keep the previous answer visible while refetching so controls do not
      // flicker between enabled and disabled on every context change.
      return { entries: { ...state.entries, [key]: { ...previous, status: "loading", error: undefined } } };
    }),

  setPermissions: (scope, permissions) =>
    set((state) => ({
      entries: { ...state.entries, [toScopeKey(scope)]: { permissions, status: "loaded", error: undefined } },
      isAdmin: permissions.isAdmin,
    })),

  setError: (scope, error) =>
    set((state) => {
      const key = toScopeKey(scope);
      const previous = state.entries[key] ?? emptyEntry;
      // The stale answer is deliberately discarded: continuing to gate on it after
      // a failure would keep showing affordances the server may have just revoked.
      return { entries: { ...state.entries, [key]: { ...previous, permissions: undefined, status: "error", error } } };
    }),

  invalidate: (scope) =>
    set((state) => {
      if (scope === undefined) {
        return { entries: {}, refetchNonce: state.refetchNonce + 1 };
      }
      const { [toScopeKey(scope)]: _removed, ...rest } = state.entries;
      return { entries: rest, refetchNonce: state.refetchNonce + 1 };
    }),

  clear: () => set({ entries: {}, activeScope: emptyScope, isAdmin: false }),
}));

const readEntry = (state: PermissionStore, scope: PermissionScope): PermissionEntry => state.entries[toScopeKey(scope)] ?? emptyEntry;

export const selectPermissionEntry =
  (scope: PermissionScope) =>
  (state: PermissionStore): PermissionEntry =>
    readEntry(state, scope);

export const selectActivePermissions = (state: PermissionStore): MePermissions | undefined => readEntry(state, state.activeScope).permissions;

export const selectActivePermissionStatus = (state: PermissionStore): PermissionFetchStatus => readEntry(state, state.activeScope).status;

/**
True while the active scope has no settled answer, so scope-dependent controls should wait.
*/
// eslint-disable-next-line unicorn/consistent-boolean-name -- repo convention: every store selector is prefixed `select`, boolean or not
export const selectIsActivePermissionPending = (state: PermissionStore): boolean => {
  const status = readEntry(state, state.activeScope).status;
  return status === "idle" || status === "loading";
};

/**
 * Whether the signed-in user may perform a team action in the active scope.
 *
 * Pure and absence-biased: an unresolved, failed or missing scope answers `false`.
 * This is an affordance check only - the server re-evaluates every call, and a
 * `true` here is never a reason to skip handling a 403.
 */
// eslint-disable-next-line unicorn/consistent-boolean-name -- curried selector factory `(action) => (state) => boolean`, not itself a boolean
export const canPerformTeamAction =
  (action: TeamAction) =>
  (state: PermissionStore): boolean =>
    selectActivePermissions(state)?.teamActions.includes(action) ?? false;

/**
Project-scope counterpart of {@link canPerformTeamAction}, with the same caveats.
*/
// eslint-disable-next-line unicorn/consistent-boolean-name -- curried selector factory `(action) => (state) => boolean`, not itself a boolean
export const canPerformProjectAction =
  (action: ProjectAction) =>
  (state: PermissionStore): boolean =>
    selectActivePermissions(state)?.projectActions.includes(action) ?? false;

export const PERMISSION_SCOPE_KEY_FOR_TESTS = toScopeKey;
/**
Keys a scope the same way the store's internal cache does, for callers that fetch on demand (e.g. `useTeamPermission`/`useProjectPermission`).
*/
export const toPermissionScopeKey = toScopeKey;
export type { PermissionStore, PermissionEntry };
